import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Bookmark, Heart, StickyNote, ArrowRight, Crown, Check, LogOut, Rocket, Plus, Building2, Handshake } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { useNotebook } from "../hooks/useNotebook";
import { usePro } from "../hooks/usePro";
import { useAuth } from "../hooks/useAuth";
import { PageRocket } from "../components/PageRocket";
import { PreviewProjectCard, RequestCard, ComingSoonCard, ContinueBuildingCard } from "../components/PreviewProject";
import { PreviewProjectModal } from "../components/PreviewProjectModal";
import { SettingsMenu } from "../components/account/SettingsMenu";
import { fetchMyPreviewProjects, fetchMyPreviewRequests, fetchMyContinueBuilding, notifyInterest, resourcesFavorites } from "../lib/api";

export default function Profile() {
  const navigate = useNavigate();
  const { data } = useNotebook();
  const { isPro, openUpgrade } = usePro();
  const { user, logout, startLogin } = useAuth();
  const [builds, setBuilds] = useState([]);
  const [requests, setRequests] = useState([]);
  const [buildOpen, setBuildOpen] = useState(false);
  const [cb, setCb] = useState(null);
  const [favResources, setFavResources] = useState([]);

  const loadBuilds = useCallback(() => {
    if (!user) {
      setBuilds([]);
      setRequests([]);
      setCb(null);
      setFavResources([]);
      return;
    }
    fetchMyPreviewProjects().then(setBuilds).catch(() => setBuilds([]));
    fetchMyPreviewRequests().then(setRequests).catch(() => setRequests([]));
    fetchMyContinueBuilding().then(setCb).catch(() => setCb(null));
    resourcesFavorites().then((d) => setFavResources(d.tools || [])).catch(() => setFavResources([]));
  }, [user]);

  useEffect(() => {
    loadBuilds();
  }, [loadBuilds]);

  const notify = async (label) => {
    try {
      await notifyInterest(label);
    } catch {
      /* ignore */
    }
    toast.success("You're on the list 🎉", { description: `We'll notify you when ${label} is available.` });
  };

  const stats = [
    { label: "Saved Ideas", value: data.saved.length, icon: Bookmark },
    { label: "Favorites", value: data.favorites.length, icon: Heart },
    { label: "Notes", value: data.notes.length, icon: StickyNote },
  ];

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center font-display text-3xl font-bold overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={user.name || "Profile"} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                "L"
              )}
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0F0F0F]">
                {user ? user.name || "Founder" : "Founder Profile"}
              </h1>
              <p className="text-[#687280] mt-1">
                {user ? user.email : "Sign in to save ideas and build."}
              </p>
              {user && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={logout}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#687280] hover:text-red-500 transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                  <SettingsMenu />
                </div>
              )}
            </div>
          </div>
          <PageRocket className="w-32 lg:w-48" />
        </div>

        {!user && (
          <div className="bg-[#F7F7F7] rounded-2xl p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="profile-signin-card">
            <div>
              <p className="font-display font-semibold text-[#0F0F0F]">You're browsing as a guest</p>
              <p className="text-sm text-[#687280]">Sign in with Google to save ideas, take notes, and build.</p>
            </div>
            <button
              onClick={startLogin}
              className="inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors shrink-0"
              data-testid="profile-signin-btn"
            >
              Continue with Google
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                data-testid={`profile-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center mb-4">
                  <Icon size={20} />
                </div>
                <p className="font-display text-3xl font-bold text-[#0F0F0F]">{s.value}</p>
                <p className="text-sm text-[#687280] mt-1">{s.label}</p>
              </div>
            );
          })}
        </div>

        {user && (
          <div className="mb-12" data-testid="my-preview-projects-section">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center">
                  <Rocket size={18} />
                </div>
                <h2 className="font-display text-2xl font-bold text-[#0F0F0F]">My Preview Projects</h2>
              </div>
              <button
                onClick={() => setBuildOpen(true)}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors"
                data-testid="profile-new-build-btn"
              >
                <Plus size={16} /> Request a Preview Project
              </button>
            </div>

            {builds.length === 0 && requests.length === 0 ? (
              <div className="bg-[#F7F7F7] rounded-2xl p-8 text-center" data-testid="no-builds-state">
                <div className="w-12 h-12 rounded-2xl bg-white text-[#D4AF37] flex items-center justify-center mx-auto mb-3">
                  <Rocket size={22} />
                </div>
                <p className="font-display font-semibold text-[#0F0F0F]">Turn your idea into a live preview</p>
                <p className="text-sm text-[#687280] mt-1 max-w-md mx-auto">
                  Request a designed, interactive preview of your idea. Our team reviews it, and once approved you can
                  secure your preview and track its progress right here.
                </p>
                <button
                  onClick={() => setBuildOpen(true)}
                  className="mt-5 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
                  data-testid="preview-request-cta"
                >
                  <Plus size={16} /> Request a Preview Project
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {requests.map((r) => (
                  <RequestCard key={r.id} request={r} />
                ))}
                {builds.map((b) => (
                  <PreviewProjectCard key={b.id} project={b} />
                ))}
              </div>
            )}
          </div>
        )}

        {user && (
          <div className="mb-12" data-testid="continue-building-section">
            <h2 className="font-display text-2xl font-bold text-[#0F0F0F] mb-6">Next steps &amp; services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ContinueBuildingCard project={cb} />
              <ComingSoonCard
                icon={Building2}
                title="Full Development"
                description="Our complete, done-for-you development service is being prepared. Join the waitlist."
                onNotify={() => notify("Full Development")}
                testid="cs-full-development"
              />
              <ComingSoonCard
                icon={Handshake}
                title="Development Partnership"
                description="A monthly dedicated development partnership for scaling founders. Join the waitlist."
                onNotify={() => notify("Development Partnership")}
                testid="cs-partnership"
              />
            </div>
          </div>
        )}

        {isPro ? (
          <div className="bg-[#0F0F0F] rounded-3xl p-10 text-center" data-testid="profile-pro-card">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] px-4 py-1.5 text-xs font-semibold mb-4">
              <Crown size={14} /> LAUNCHBS PRO MEMBER
            </div>
            <h2 className="font-display text-2xl font-bold text-white">You're all set to build.</h2>
            <p className="text-[#A1A1AA] mt-2 max-w-md mx-auto">
              Unlimited saves, notebook, notes and AI build prompts are unlocked.
            </p>
            <button
              onClick={() => navigate("/business-ideas")}
              className="mt-7 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors"
              data-testid="profile-browse-btn"
            >
              Browse Ideas <ArrowRight size={18} />
            </button>
          </div>
        ) : (
          <div className="bg-[#0F0F0F] rounded-3xl p-10 text-center" data-testid="profile-upgrade-card">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 text-[#D4AF37] px-4 py-1.5 text-xs font-semibold mb-4">
              <Crown size={14} /> LAUNCHBS PRO
            </div>
            <h2 className="font-display text-2xl font-bold text-white">Turn inspiration into action.</h2>
            <p className="text-[#A1A1AA] mt-2 max-w-md mx-auto">
              Save unlimited ideas, keep notes, and build businesses with AI prompts.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-sm text-[#A1A1AA]">
              {["Unlimited saves", "Startup Notebook", "AI build prompts", "Priority support"].map((f) => (
                <span key={f} className="inline-flex items-center gap-2">
                  <Check size={14} className="text-[#D4AF37]" /> {f}
                </span>
              ))}
            </div>
            <button
              onClick={() => openUpgrade("default")}
              className="mt-7 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors"
              data-testid="profile-upgrade-btn"
            >
              <Crown size={18} /> Upgrade to LaunchBS Pro
            </button>
            <p className="text-xs text-[#687280] mt-3">$9.99/month or $99/year · Save 17%</p>
          </div>
        )}
      </section>

      <PreviewProjectModal
        open={buildOpen}
        onClose={() => setBuildOpen(false)}
        onSubmitted={loadBuilds}
      />
    </Layout>
  );
}
